import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/AutocompleteLocation.css";
import * as Sentry from "@sentry/react";

function AutocompleteLocation({
    ref = null,
    id = "acl-location",
    name = "location",
    placeholder = "(ex: St. Louis, MO)",
    setLocation = (newloc) => {},
    setIsValid = () => {},
    overrideValue = "",
    disabled = false,
    hidden = false,
    maxSuggestions = 25
}) {
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [allSuggest, setAllSuggest] = useState([]); // ["City, ST", ...]
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [userTouched, setUserTouched] = useState(false);

    const containerRef = useRef(null);

    const normalize = (s) => (s || "").trim().toLowerCase();

    const allSuggestSet = useMemo(() => {
        return new Set(allSuggest.map((s) => normalize(s)));
    }, [allSuggest]);

    const isExactValid = (val) => {
        const v = normalize(val);
        return v.length > 0 && allSuggestSet.has(v);
    };

    const buildSuggestions = (value) => {
        const v = (value || "").trim();
        const lowerVal = v.toLowerCase();

        if (!lowerVal) {
            return allSuggest.slice(0, maxSuggestions);
        }

        const hits = allSuggest.filter((name) => name.toLowerCase().includes(lowerVal));
        return hits.slice(0, maxSuggestions);
    };

    /**
     * Load CSV ONCE
     */
    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                setLoading(true);
                const res = await fetch("/static/uscities.csv", { signal: controller.signal });
                if (!res.ok) {
                    console.error("AutocompleteLocation: failed to fetch /static/uscities.csv");
                    return;
                }

                const text = await res.text();
                const rows = text.split("\n");

                const out = [];
                for (let i = 1; i < rows.length; i++) {
                    const row = rows[i];
                    if (!row) continue;

                    // Your current approach is split by comma.
                    // NOTE: this will break if your CSV has quoted commas in city names.
                    // If that happens, we can switch to a real CSV parser.
                    const cols = row.split(",");
                    if (cols.length < 3) continue;

                    const city = (cols[1] || "").trim();
                    const state = (cols[2] || "").trim();
                    if (!city || !state) continue;

                    out.push(`${city}, ${state}`);
                }

                // Deduplicate while preserving order
                const seen = new Set();
                const deduped = [];
                for (const item of out) {
                    const key = normalize(item);
                    if (!seen.has(key)) {
                        seen.add(key);
                        deduped.push(item);
                    }
                }

                setAllSuggest(deduped);
            } catch (err) {
                if (err.name !== "AbortError") {
                    Sentry.captureException(err);
                    console.error("AutocompleteLocation fetch error:", err);
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, []);

    /**
     * Apply overrideValue (from backend)
     */
    useEffect(() => {
        const v = overrideValue || "";
        setInput(v);

        const ok = v ? isExactValid(v) : false;
        setIsValid(ok);

        if (ok) {
            setLocation(v);
        }
    }, [overrideValue, allSuggestSet]);

    /**
     * Close dropdown on outside click
     */
    useEffect(() => {
        const handleClick = (event) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target)) {
                setOpen(false);
                setFilteredSuggestions([]);
            }
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const selectValue = (val) => {
        setUserTouched(true);
        setInput(val);
        setLocation(val);
        setIsValid(true);
        setOpen(false);
        setFilteredSuggestions([]);
    };

    const handleChange = (val) => {
        setUserTouched(true);
        setInput(val);

        // typing = invalid unless exact match
        const ok = isExactValid(val);
        setIsValid(ok);
        if (ok) {
            setLocation(val);
        } else {
            // optional: keep parent location in sync with typing (but not valid)
            setLocation(val);
        }

        const sugg = buildSuggestions(val);
        setFilteredSuggestions(sugg);
        setOpen(true);
    };

    const handleFocus = () => {
        if (disabled) return;
        setUserTouched(true);
        const sugg = buildSuggestions(input);
        setFilteredSuggestions(sugg);
        setOpen(true);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();

            // If exact match, accept
            if (isExactValid(input)) {
                selectValue(input);
                return;
            }

            // Otherwise pick first suggestion if exists
            if (filteredSuggestions.length > 0) {
                selectValue(filteredSuggestions[0]);
                return;
            }

            // No suggestion -> invalid
            setIsValid(false);
            setOpen(false);
            setFilteredSuggestions([]);
        } else if (event.key === "Escape") {
            setOpen(false);
            setFilteredSuggestions([]);
        }
    };

    const handleBlur = () => {
        // On blur, snap to exact match OR first suggestion OR invalidate
        if (disabled) return;

        if (isExactValid(input)) {
            setIsValid(true);
            setLocation(input);
            return;
        }

        if (filteredSuggestions.length > 0) {
            selectValue(filteredSuggestions[0]);
            return;
        }

        setIsValid(false);
    };

    if (hidden) {
        return null;
    }

    return (
        <div className="acl-container" ref={containerRef}>
            <div className="acl-input-wrap">
                <input
                    value={loading ? "..." : input}
                    placeholder={placeholder}
                    type="text"
                    id={id}
                    name={name}
                    ref={ref}
                    disabled={disabled}
                    autoComplete="off"
                    className="acl-input"
                    aria-label="Enter a location: City, State"
                    onChange={(event) => handleChange(event.target.value)}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />

                {!disabled && open && filteredSuggestions.length > 0 && (
                    <ul className="acl-dropdown" role="listbox">
                        {filteredSuggestions.map((item, i) => (
                            <li
                                key={`${item}-${i}`}
                                className="acl-item"
                                onMouseDown={(e) => {
                                    // prevent blur firing before click selection
                                    e.preventDefault();
                                }}
                                onClick={() => selectValue(item)}
                                role="option"
                                aria-selected={normalize(item) === normalize(input)}
                            >
                                {input
                                    ? item.split(new RegExp(`(${input})`, "gi")).map((part, idx) =>
                                          part.toLowerCase() === input.toLowerCase() ? (
                                              <span key={idx} className="acl-highlight">
                                                  {part}
                                              </span>
                                          ) : (
                                              <span key={idx}>{part}</span>
                                          )
                                      )
                                    : item}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Optional: you can show a tiny hint when user typed something invalid */}
            {userTouched && input && !isExactValid(input) && (
                <div className="acl-hint">
                    Select a location from the dropdown
                </div>
            )}
        </div>
    );
}

export { AutocompleteLocation };
